import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { Button, Badge, Skeleton, TooltipDirective } from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { LocationNamePipe } from '../../../locations/pipes/location-name-pipe';
import { DatePipe, NgClass } from '@angular/common';
import { SupplierContractScoreColorPipe } from '../../pipes/supplier-contract-score-color-pipe';
import {
  getEnergyInterfaces,
  getLocations,
  SupplierContractData,
} from '../../stores/supplier-contracts.store';
import { SupplierContractPriceRangePipe } from '../../pipes/supplier-contract-price-range-pipe';
import { GermanNumberPipe } from '../../../../shared/pipes/german-number-pipe';
import { LocationsStore } from '../../../locations/store/locations.store';
import { EnergyInterfacesStore } from '../../../energy-interfaces/stores/energy-interfaces.store';
import { FORECAST_LIMIT } from '../../definitions';
import { isNotNull } from '../../../../core/helper/type-helper';
import {
  ForecastKey,
  SupplierContractForecastStore,
} from '../../stores/supplier-contract-forecast.store';
import { ErrorComponent } from '../../../../shared/components/error-component/error-component';
import { toHttpError } from '../../../../core/helper/http-error-type-guard';
import { SupplierContractCardSkeleton } from '../supplier-contract-card-skeleton/supplier-contract-card-skeleton';

@Component({
  selector: 'app-supplier-contract-card',
  imports: [
    Button,
    NgIcon,
    RouterLink,
    Badge,
    LocationNamePipe,
    TranslocoPipe,
    DatePipe,
    SupplierContractScoreColorPipe,
    SupplierContractPriceRangePipe,
    GermanNumberPipe,
    Skeleton,
    NgClass,
    TooltipDirective,
    ErrorComponent,
    SupplierContractCardSkeleton,
  ],
  templateUrl: './supplier-contract-card.html',
})
export class SupplierContractCard {
  private locationsStore = inject(LocationsStore);
  private energyInterfacesStore = inject(EnergyInterfacesStore);
  private forecastStore = inject(SupplierContractForecastStore);

  supplierContract = input.required<SupplierContractData>();
  fullPrice = input.required<boolean>();

  currentYear = input.required<number>();

  protected price = computed(() => {
    const id = this.supplierContract().id;
    const year = this.currentYear();
    const key: ForecastKey = `${id}-${year}-ALL`;
    return this.forecastStore.prices()[key] ?? null;
  });
  protected priceErrorStatus = computed(() => {
    const error = toHttpError(this.price()?.error);
    if (error === null) {
      return null;
    }
    return error.status;
  });

  protected cost = computed(() => {
    const id = this.supplierContract().id;
    const year = this.currentYear();
    const key: ForecastKey = `${id}-${year}-ALL`;
    return this.forecastStore.cost()[key] ?? null;
  });
  protected costErrorStatus = computed(() => {
    const error = toHttpError(this.cost()?.error);
    if (error === null) {
      return null;
    }
    return error.status;
  });

  protected totalConsumption = computed(
    () => this.price()?.data?.totalConsumption ?? this.cost()?.data?.totalConsumption ?? 0,
  );

  protected totalPriceWithoutTaxes = computed(
    () => this.price()?.data?.totalPriceWithoutTaxes ?? { min: 0, max: 0 },
  );
  protected totalPriceWithTaxes = computed(
    () => this.price()?.data?.totalPrice ?? { min: 0, max: 0 },
  );

  protected totalEnergyCostWithoutTaxes = computed(
    () => this.cost()?.data?.totalEnergyCostWithoutTaxes ?? { min: 0, max: 0 },
  );
  protected totalEnergyCostWithTaxes = computed(
    () => this.cost()?.data?.totalEnergyCost ?? { min: 0, max: 0 },
  );

  protected locationsLoading = this.locationsStore.isLoading;
  protected locations = computed(() => {
    const ids = getLocations(
      this.supplierContract(),
      this.energyInterfacesStore.energyInterfaces() ?? [],
    );
    const allLocations = this.locationsStore.locations() ?? [];
    return ids
      .map((id) => allLocations.find((location) => location.id === id)?.data)
      .filter(isNotNull);
  });
  protected energyInterfacesLoading = this.energyInterfacesStore.isLoading;
  protected energyInterfaces = computed(() =>
    getEnergyInterfaces(
      this.supplierContract(),
      this.energyInterfacesStore.energyInterfaces() ?? [],
    ),
  );

  protected showFutureBadge = computed(
    () => new Date(this.supplierContract().contractStartDate) > new Date(),
  );
  protected isEnded = computed(() => {
    const end = this.supplierContract().contractEndDate;
    return end && new Date(end) < new Date();
  });

  protected isPast = computed(() => this.currentYear() < new Date().getFullYear());
  protected isAvailableForForecast = computed(
    () =>
      this.currentYear() >= new Date().getFullYear() &&
      this.currentYear() <= new Date().getFullYear() + FORECAST_LIMIT,
  );

  /**
   * The latest possible termination date is the day before the end of the termination period, as the contract can be terminated until the end of the day before the termination period ends. For example, if the termination period ends on 2024-12-31, the latest possible termination date is 2024-12-30.
   */
  protected latestPossibleTerminationDate = computed(() => {
    const terminationPeriodEndDate = this.supplierContract().terminationPeriodEndDate;
    if (!terminationPeriodEndDate) {
      return null;
    }
    const endDate = new Date(terminationPeriodEndDate);
    endDate.setUTCDate(endDate.getUTCDate() - 1);
    return endDate;
  });

  protected contractCheckAlerts = computed(() => this.supplierContract().latestCheckResult);

  constructor() {
    effect(() => {
      if (!this.isAvailableForForecast()) {
        return;
      }
      const id = this.supplierContract().id;
      const year = this.currentYear();

      if (!untracked(this.price)) {
        this.forecastStore.loadPrice(id, year, null);
      }
      if (!untracked(this.cost)) {
        this.forecastStore.loadCost(id, year, null);
      }
    });
  }

  protected onClickRefresh() {
    const id = this.supplierContract().id;
    const year = this.currentYear();

    this.forecastStore.loadPrice(id, year, null);
    this.forecastStore.loadCost(id, year, null);
  }
}
